import { useState } from "react";
import { useLocation } from "react-router-dom";
import { useLang } from "../contexts/LanguageContext";
import { api, formatApiErrorDetail } from "../lib/api";
import { Calendar, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const SERVICE_TYPES = [
    "Maintenance",
    "Repair",
    "ITV",
    "Track Prep",
    "Custom Work",
    "Diagnostics",
];

const SERVICE_LABELS = {
    es: {
        Maintenance: "Mantenimiento",
        Repair: "Reparación",
        ITV: "ITV",
        "Track Prep": "Preparación de pista",
        "Custom Work": "Trabajos custom",
        Diagnostics: "Diagnóstico",
    },
    en: {
        Maintenance: "Maintenance",
        Repair: "Repair",
        ITV: "ITV",
        "Track Prep": "Track Prep",
        "Custom Work": "Custom Work",
        Diagnostics: "Diagnostics",
    },
};


const CHECK_LIST = {
    es: [
        "Mantenimiento y diagnóstico oficial",
        "Transparencia total",
        "Seguridad y rendimiento",
    ],
    en: [
        "Official maintenance & diagnostics",
        "Full transparency",
        "Safety and performance",
    ],
};

const SUBTITLE = {
    es: "Cuidamos de tu Yamaha con criterio técnico, transparencia y sin improvisaciones. Queremos que tu moto responda siempre como el primer día, ya sea para el día a día en la ciudad o para disfrutar de la carretera.",
    en: "We take care of your Yamaha with technical expertise, full transparency, and no improvisation. We want your bike to perform like new every day — in the city or out on the open road.",
};

export default function Service() {
    const { lang, t } = useLang();
    const { state } = useLocation();
    const initialServiceType = SERVICE_TYPES.includes(state?.serviceType) ? state.serviceType : SERVICE_TYPES[0];
    const [form, setForm] = useState({
        name: "",
        email: "",
        phone: "",
        motorcycle_model: "",
        service_type: initialServiceType,
        preferred_date: "",
        preferred_time: "10:00",
        notes: "",
    });
    const [submitting, setSubmitting] = useState(false);
    const [done, setDone] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            await api.post("/service-booking", form);
            setDone(true);
            toast.success(t.forms.success);
            setForm({ ...form, name: "", email: "", phone: "", motorcycle_model: "", notes: "" });
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
        } finally {
            setSubmitting(false);
        }
    };

    const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    return (
        <div data-testid="service-page">
            <section className="mx-auto max-w-7xl px-6 py-8 lg:px-10 lg:py-20">

                {/* Grid — 3 items: title / form / info */}
                <div className="grid grid-cols-1 gap-8 lg:grid-cols-2 lg:gap-x-20 lg:gap-y-4">

                    {/* TITLE — mobile row 1, desktop col 1 row 1 */}
                    <div className="order-1 lg:col-start-1 lg:row-start-1 lg:self-end">
                        <p className="text-overline">{t.service_cards.overline}</p>
                        <h1 className="mt-3 font-display text-4xl font-bold tracking-tighter leading-[0.95] sm:text-5xl lg:text-6xl">
                            {lang === "es"
                                ? "Reserva tu Cita de Taller Especializado"
                                : "Book Your Expert Workshop Appointment"}
                        </h1>
                    </div>

                    {/* INFO — mobile row 3, desktop col 1 row 2 */}
                    <div className="order-3 lg:col-start-1 lg:row-start-2" data-testid="service-info-col">
                        <p className="text-sm text-neutral-600 leading-relaxed">
                            {SUBTITLE[lang]}
                        </p>
                        <ul className="mt-6 space-y-3">
                            {CHECK_LIST[lang].map((label, i) => (
                                <li key={i} className="flex items-center gap-3">
                                    <CheckCircle2 size={16} className="shrink-0" style={{ color: "var(--ls-terracotta)" }} />
                                    <span className="text-sm font-medium text-neutral-700">{label}</span>
                                </li>
                            ))}
                        </ul>
                        <p className="mt-8 text-sm text-neutral-500 leading-relaxed border-t border-black/10 pt-6">
                            {lang === "es" ? t.sections.service_long_es : t.sections.service_long_en}
                        </p>
                    </div>

                    {/* FORM — mobile row 2, desktop col 2 rows 1-2 */}
                    <div className="order-2 lg:col-start-2 lg:row-start-1 lg:row-span-2 lg:sticky lg:top-24" data-testid="service-booking-section">
                        <div className="glass border border-black/10 p-5 lg:p-8">
                            <p className="text-overline">{t.forms.service_title}</p>
                            <h2 className="mt-2 font-display text-xl font-bold tracking-tight lg:text-2xl">
                                {lang === "es"
                                    ? "¿Qué necesita tu Yamaha?"
                                    : "What does your Yamaha need?"}
                            </h2>

                            <form onSubmit={submit} className="mt-5 space-y-3" data-testid="service-form">
                                <div className="grid grid-cols-2 gap-3">
                                    <Field label={t.forms.name} value={form.name} onChange={onChange("name")} required testid="service-name" />
                                    <Field label={t.forms.email} type="email" value={form.email} onChange={onChange("email")} required testid="service-email" />
                                    <Field label={t.forms.phone} value={form.phone} onChange={onChange("phone")} required testid="service-phone" />
                                    <Field label={t.forms.motorcycle_model} value={form.motorcycle_model} onChange={onChange("motorcycle_model")} required testid="service-model" />

                                    <div className="flex flex-col gap-1">
                                        <label className="font-mono text-[9px] uppercase tracking-[0.25em] text-neutral-500">
                                            {t.forms.service_type}
                                        </label>
                                        <select
                                            value={form.service_type}
                                            onChange={onChange("service_type")}
                                            data-testid="service-type-select"
                                            className="bg-transparent border-b border-black/20 py-2 text-sm text-neutral-900 focus:outline-none focus:border-[--ls-blue]"
                                        >
                                            {SERVICE_TYPES.map((s) => (
                                                <option key={s} value={s} className="bg-[--ls-bg]">{SERVICE_LABELS[lang][s]}</option>
                                            ))}
                                        </select>
                                    </div>

                                    <Field label={t.forms.preferred_date} type="date" value={form.preferred_date} onChange={onChange("preferred_date")} required testid="service-date" />
                                    <Field label={t.forms.preferred_time} type="time" value={form.preferred_time} onChange={onChange("preferred_time")} required testid="service-time" />
                                </div>

                                <div className="flex flex-col gap-1">
                                    <label className="font-mono text-[9px] uppercase tracking-[0.25em] text-neutral-500">
                                        {t.forms.notes}
                                    </label>
                                    <textarea
                                        value={form.notes}
                                        onChange={onChange("notes")}
                                        rows={2}
                                        data-testid="service-notes"
                                        className="bg-transparent border-b border-black/20 py-2 text-sm text-neutral-900 focus:outline-none focus:border-[--ls-blue] resize-none"
                                    />
                                </div>

                                <button
                                    type="submit"
                                    disabled={submitting}
                                    data-testid="service-submit-btn"
                                    className="btn-primary disabled:opacity-50 w-full justify-center mt-1"
                                >
                                    <Calendar size={15} />
                                    {submitting ? t.forms.sending : t.forms.book}
                                </button>

                                {done && (
                                    <p className="font-mono text-xs uppercase tracking-[0.2em] text-center" style={{ color: "var(--ls-terracotta)" }}>
                                        ✓ {t.forms.success}
                                    </p>
                                )}
                            </form>
                        </div>
                    </div>

                </div>
            </section>
        </div>
    );
}

function Field({ label, value, onChange, type = "text", required = false, testid }) {
    return (
        <div className="flex flex-col gap-1">
            <label className="font-mono text-[9px] uppercase tracking-[0.25em] text-neutral-500">{label}</label>
            <input
                type={type}
                value={value}
                onChange={onChange}
                required={required}
                data-testid={testid}
                className="bg-transparent border-b border-black/20 py-2 text-sm text-neutral-900 focus:outline-none focus:border-[--ls-blue]"
            />
        </div>
    );
}
